/**
 * EnhancedLogsTable Component
 * Uses lift-ui DataTable with Load More button at bottom of scroll area
 */

import React, { useState, useMemo } from 'react';
import {
  User,
  Clock,
  Globe,
  Monitor,
  AlertCircle,
  CheckCircle2,
  Activity,
  Loader2,
  ChevronDown,
  ArrowRight,
  Plus,
  Minus,
  ListChecks,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  DataTable,
  Button,
} from 'lift-ui';
import type { TableColumn } from 'lift-ui';
import { AdminLogEntry, LogChange, LogDetail } from '../../types';
import { formatTimestamp, highlightText, getCategoryInfo } from '../../utils';
import { cn } from '@/lib/utils';

interface EnhancedLogsTableProps {
  logs: AdminLogEntry[];
  searchText: string;
  isLoading?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
  hasMore?: boolean;
}

const CategoryBadge = ({ category }: { category: AdminLogEntry['action']['category'] }) => {
  const info = getCategoryInfo(category);
  
  return (
    <Badge 
      variant="outline" 
      className="text-xs font-normal bg-secondary text-secondary-foreground border-border"
    >
      {info.label}
    </Badge>
  );
};

const HighlightedText = ({ text, search }: { text: string; search: string }) => {
  const { parts } = highlightText(text, search);
  
  return (
    <>
      {parts.map((part, i) => (
        <span
          key={i}
          className={part.highlight ? 'bg-primary/20 text-foreground font-medium' : ''}
        >
          {part.text}
        </span>
      ))}
    </>
  );
};

export function EnhancedLogsTable({
  logs,
  searchText,
  isLoading,
  isLoadingMore,
  onLoadMore,
  hasMore,
}: EnhancedLogsTableProps) {
  const [selectedLog, setSelectedLog] = useState<AdminLogEntry | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  const handleViewLog = (log: AdminLogEntry) => {
    setSelectedLog(log);
    setSheetOpen(true);
  };


  // Define columns for lift-ui DataTable
  const columns = useMemo((): TableColumn<AdminLogEntry>[] => [
    {
      key: 'user',
      header: 'User',
      accessor: (row) => row.user.name,
      width: '20%',
      render: (_value, row) => (
        <div className="flex items-center gap-3">
          {row.user.image ? (
            <img
              src={row.user.image}
              alt={row.user.name}
              className="h-8 w-8 rounded-full object-cover flex-shrink-0 ring-1 ring-border"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                e.currentTarget.nextElementSibling?.classList.remove('hidden');
              }}
            />
          ) : null}
          <div className={cn(
            "h-8 w-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0",
            row.user.image && "hidden"
          )}>
            <User className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-medium text-sm truncate">
              <HighlightedText text={row.user.name} search={searchText} />
            </div>
            <div className="text-xs text-muted-foreground truncate">
              <HighlightedText text={row.user.email} search={searchText} />
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'action',
      header: 'Action',
      accessor: (row) => row.action.description,
      width: '40%',
      render: (_value, row) => (
        <div className="max-w-md">
          <div 
            className={cn(
              'text-sm line-clamp-2',
              row.severity === 'ERROR' && 'text-destructive font-medium'
            )}
            title={row.action.description}
          >
            {row.action.description}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            {row.action.type}
          </div>
        </div>
      ),
    },
    {
      key: 'category',
      header: 'Category',
      accessor: (row) => row.action.category,
      width: '12%',
      render: (_value, row) => (
        <CategoryBadge category={row.action.category} />
      ),
    },
    {
      key: 'status',
      header: 'Status',
      accessor: (row) => row.result?.success !== false ? 'Success' : 'Error',
      width: '12%',
      render: (_value, row) => (
        <div className="flex items-center gap-1.5">
          {row.result?.success !== false ? (
            <CheckCircle2 className="h-4 w-4 text-[#334238]" />
          ) : (
            <AlertCircle className="h-4 w-4 text-red-500" />
          )}
          <Badge
            variant="outline"
            className={cn(
              'text-xs font-medium',
              row.result?.success !== false 
                ? 'bg-[#CCE561]/20 text-[#334238] border-[#CCE561]'
                : 'bg-red-50 text-red-700 border-red-200'
            )}
          >
            {row.result?.success !== false ? 'Info' : 'Error'}
          </Badge>
        </div>
      ),
    },
    {
      key: 'timestamp',
      header: 'Time',
      accessor: (row) => row.timestamp,
      width: '16%',
      align: 'right' as const,
      render: (_value, row) => (
        <span className="text-sm text-muted-foreground whitespace-nowrap">
          {formatTimestamp(row.timestamp, 'relative')}
        </span>
      ),
    },
  ], [searchText]);

  // Show footer when we have logs
  const showFooter = !isLoading && logs.length > 0;

  return (
    <>
      {/* Main container - flex column with min-h-0 is CRITICAL for scroll to work */}
      <div className="flex flex-col h-full min-h-0">
        {/* DataTable - flex-1 min-h-0 overflow-auto allows proper scrolling */}
        <DataTable<AdminLogEntry>
          className="flex-1 min-h-0 overflow-auto"
          maxHeight="100%"
          data={logs}
          columns={columns}
          loading={isLoading}
          emptyMessage="No logs found. Try adjusting your filters or search terms."
          stickyHeader
          showActions={false}
          rowClickable
          onRowClick={(row) => handleViewLog(row)}
        />
        
        {/* Load More Footer - fixed height, won't shrink */}
        {showFooter && (
          <div className="h-14 border-t bg-white flex items-center justify-center gap-3 px-4 flex-shrink-0">
            <span className="text-sm text-muted-foreground">
              Showing {logs.length.toLocaleString()} logs
            </span>
            {hasMore ? (
              <Button
                variant="outline"
                size="sm"
                onClick={onLoadMore}
                disabled={isLoadingMore}
                className="gap-2"
              >
                {isLoadingMore ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading...
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-4 w-4" />
                    Load More
                  </>
                )}
              </Button>
            ) : (
              <span className="text-sm text-[#334238] font-medium">
                All loaded
              </span>
            )}
          </div>
        )}
      </div>

      {/* Log Detail Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen} modal={true}>
        <SheetContent 
          side="right" 
          className="w-[600px] sm:w-[700px] sm:max-w-2xl overflow-y-auto bg-white z-50"
        >
          <SheetHeader className="border-b border-border pb-4">
            <SheetTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              Log Details
            </SheetTitle>
            {selectedLog && (
              <SheetDescription>
                {formatTimestamp(selectedLog.timestamp, 'full')}
              </SheetDescription>
            )}
          </SheetHeader>

          {selectedLog && (
            <div className="py-6 space-y-6">
              {/* User Info */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">User</h4>
                <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                  {selectedLog.user.image ? (
                    <img
                      src={selectedLog.user.image}
                      alt={selectedLog.user.name}
                      className="h-10 w-10 rounded-full object-cover"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        e.currentTarget.nextElementSibling?.classList.remove('hidden');
                      }}
                    />
                  ) : null}
                  <div className={cn(
                    "h-10 w-10 rounded-full bg-muted flex items-center justify-center",
                    selectedLog.user.image && "hidden"
                  )}>
                    <User className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <div>
                    <div className="font-medium">{selectedLog.user.name}</div>
                    <div className="text-sm text-muted-foreground">{selectedLog.user.email}</div>
                  </div>
                </div>
              </div>

              {/* Action */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Action</h4>
                <div className="p-3 bg-muted/50 rounded-lg space-y-2">
                  <div className={cn(
                    'font-medium break-words',
                    selectedLog.severity === 'ERROR' && 'text-destructive'
                  )}>
                    {selectedLog.action.description}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className="text-xs">{selectedLog.action.type}</Badge>
                    <CategoryBadge category={selectedLog.action.category} />
                  </div>
                </div>
              </div>

              {/* Changes (updates) */}
              {Array.isArray(selectedLog.payload?.changes) && selectedLog.payload!.changes.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Changes</h4>
                  <div className="p-3 bg-muted/50 rounded-lg space-y-3">
                    {selectedLog.payload!.changes.map((change: LogChange, i: number) => {
                      // Prefer from → to (same as other field updates). List +/- only when no from/to.
                      const hasFromTo =
                        change.from !== undefined || change.to !== undefined;
                      const isListOnly =
                        !hasFromTo && Boolean(change.added || change.removed);

                      return (
                      <div key={i} className="text-sm">
                        <div className="font-medium text-foreground mb-1">{change.label}</div>
                        {isListOnly ? (
                          <div className="space-y-1 pl-1">
                            {change.added && change.added.length > 0 && (
                              <div className="flex items-start gap-1.5 text-emerald-700">
                                <Plus className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                                <span className="break-words">{change.added.join(', ')}</span>
                              </div>
                            )}
                            {change.removed && change.removed.length > 0 && (
                              <div className="flex items-start gap-1.5 text-red-600">
                                <Minus className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                                <span className="break-words">{change.removed.join(', ')}</span>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 flex-wrap pl-1">
                            <span className="text-muted-foreground line-through break-words">{change.from}</span>
                            <ArrowRight className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                            <span className="text-foreground font-medium break-words">{change.to}</span>
                          </div>
                        )}
                      </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Details (create / delete) */}
              {Array.isArray(selectedLog.payload?.details) && selectedLog.payload!.details.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                    <ListChecks className="h-4 w-4" />
                    Details
                  </h4>
                  <div className="p-3 bg-muted/50 rounded-lg space-y-1.5">
                    {selectedLog.payload!.details.map((detail: LogDetail, i: number) => (
                      <div key={i} className="flex items-start gap-2 text-sm">
                        <span className="text-muted-foreground min-w-[140px] flex-shrink-0">{detail.label}</span>
                        <span className="text-foreground font-medium break-words">{detail.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Request Details */}
              {selectedLog.request && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Request</h4>
                  <div className="p-3 bg-muted/50 rounded-lg space-y-3">
                    <div className="flex items-start gap-2">
                      <Globe className="h-4 w-4 text-muted-foreground mt-1 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-muted-foreground mb-1">Endpoint</div>
                        <div className="flex items-start gap-2">
                          <Badge variant="outline" className="text-xs font-mono flex-shrink-0">
                            {selectedLog.request.method}
                          </Badge>
                          <span className="text-sm font-mono text-muted-foreground break-all">
                            {selectedLog.request.url}
                          </span>
                        </div>
                      </div>
                    </div>

                    {selectedLog.request.statusCode && (
                      <div className="flex items-start gap-2">
                        <Monitor className="h-4 w-4 text-muted-foreground mt-1 flex-shrink-0" />
                        <div>
                          <div className="text-xs text-muted-foreground mb-1">Response Code</div>
                          <Badge 
                            variant="outline" 
                            className={cn(
                              'text-xs font-mono font-medium',
                              selectedLog.request.statusCode >= 200 && selectedLog.request.statusCode < 300 && 'bg-emerald-50 text-emerald-700 border-emerald-200',
                              selectedLog.request.statusCode >= 300 && selectedLog.request.statusCode < 400 && 'bg-blue-50 text-blue-700 border-blue-200',
                              selectedLog.request.statusCode >= 400 && selectedLog.request.statusCode < 500 && 'bg-amber-50 text-amber-700 border-amber-200',
                              selectedLog.request.statusCode >= 500 && 'bg-red-50 text-red-700 border-red-200'
                            )}
                          >
                            {selectedLog.request.statusCode}
                          </Badge>
                        </div>
                      </div>
                    )}

                    {selectedLog.request.duration && (
                      <div className="flex items-start gap-2">
                        <Clock className="h-4 w-4 text-muted-foreground mt-1 flex-shrink-0" />
                        <div>
                          <div className="text-xs text-muted-foreground mb-1">Duration</div>
                          <span className="text-sm">{selectedLog.request.duration}ms</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Result */}
              {selectedLog.result && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Result</h4>
                  <div className={cn(
                    'p-3 rounded-lg flex items-center gap-3',
                    selectedLog.result.success 
                      ? 'bg-[#CCE561]/20 border border-[#CCE561]'
                      : 'bg-red-50 border border-red-200'
                  )}>
                    {selectedLog.result.success ? (
                      <div className="p-2 rounded-full bg-[#CCE561]/30">
                        <CheckCircle2 className="h-5 w-5 text-[#334238]" />
                      </div>
                    ) : (
                      <div className="p-2 rounded-full bg-red-100">
                        <AlertCircle className="h-5 w-5 text-red-600" />
                      </div>
                    )}
                    <div>
                      <div className={cn(
                        'font-medium',
                        selectedLog.result.success ? 'text-[#334238]' : 'text-red-700'
                      )}>
                        {selectedLog.result.success ? 'Success' : 'Failed'}
                      </div>
                      {selectedLog.result.error && (
                        <div className="text-sm text-red-600 mt-1">
                          {selectedLog.result.error}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Timestamp */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Timestamp</h4>
                <div className="p-3 bg-muted/50 rounded-lg flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">{formatTimestamp(selectedLog.timestamp, 'full')}</span>
                </div>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}
